import React from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, ChevronDown, ChevronsDown, ChevronsUp, ChevronUp, Maximize2, Minimize2, Redo2, Search, Undo2, X, XCircle } from 'lucide-react';
import { C } from '../theme.js';
import { Eyebrow, IconButton } from '../components/Primitives.jsx';
import { StepIcon } from '../components/StepIcon.jsx';
import { CATEGORIES, PHASE2_LABEL, PHASE2_STEPS, PHASE2_TOOLTIP, STEP_TYPES, pipelineOrder, stepAccent, summarizeStep } from '../data.js';

/* eslint-disable */
// ─── Flow canvas: nodes, edges, palette, mini map ──────────────────────

// Compact node geometry — tuned to fit nicely on screen.
export const NODE_W = 220;
export const NODE_H = 88;
const DRAG_MIME = 'application/x-dps-step-type';

// Palette button → canvas drag source (HTML5 drag-and-drop).
function startPaletteDrag(e, type) {
  e.dataTransfer.setData(DRAG_MIME, type);
  e.dataTransfer.setData('text/plain', type);
  e.dataTransfer.effectAllowed = 'copy';
}
const GRID = 20;
const NODE_GAP = 260; // x-distance between successive nodes (matches data.jsx layout intent)
// Blocks per row before the flow wraps to the next line. Per pipeline:
// saved as pipeline.canvas.stepsPerRow, set from the canvas control pane.
export const DEFAULT_STEPS_PER_ROW = 10;
export const MIN_STEPS_PER_ROW = 1;
export const MAX_STEPS_PER_ROW = 50;

// ── Canvas view (zoom + pan), remembered per pipeline ──────────────────
// Local-only, like the inspector dock preference in App.jsx: the view is a
// per-viewer convenience, and saving it through the pipeline autosave would
// rewrite every step on each pan. Absent = auto-fit, as for a new pipeline.
const CANVAS_VIEW_PREFIX = 'dpstudio.canvasView.';
const MIN_ZOOM = 0.4;
const MAX_ZOOM = 1.8;
function readCanvasView(pipelineId) {
  try {
    const v = JSON.parse(window.localStorage.getItem(CANVAS_VIEW_PREFIX + pipelineId));
    if (!v || ![v.zoom, v.x, v.y].every(Number.isFinite)) return null;
    return { zoom: Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, v.zoom)), pan: { x: v.x, y: v.y } };
  } catch {
    return null;
  }
}
function writeCanvasView(pipelineId, view) {
  try {
    if (view) window.localStorage.setItem(CANVAS_VIEW_PREFIX + pipelineId, JSON.stringify({ zoom: view.zoom, x: view.pan.x, y: view.pan.y }));
    else window.localStorage.removeItem(CANVAS_VIEW_PREFIX + pipelineId);
  } catch { /* quota / blocked storage */ }
}
const ROW_GAP = NODE_H + 110;        // y-distance between rows
// One step per row stacks the blocks in a single column joined by short
// vertical arrows, so the gap between rows is half the usual.
const rowGapFor = (perRow) => (perRow === 1 ? NODE_H + 55 : ROW_GAP);

// Grid layout derived from pipeline order: `perRow` blocks per row, left to
// right, wrapping to a new row below. The origin is the saved position of the
// first step so existing pipelines keep their anchor.
export function layoutGrid(steps, perRow = DEFAULT_STEPS_PER_ROW) {
  if (!steps.length) return steps;
  const ordered = pipelineOrder(steps);
  const baseX = ordered[0].x ?? 0;
  const baseY = ordered[0].y ?? 140;
  const pos = new Map(ordered.map((st, i) => [st.id, {
    x: baseX + (i % perRow) * NODE_GAP,
    y: baseY + Math.floor(i / perRow) * rowGapFor(perRow),
  }]));
  return steps.map(st => ({ ...st, ...pos.get(st.id) }));
}


// SVG dotted background pattern (drawn once via <defs>)
function CanvasBg() {
  return (
    <defs>
      <pattern id="dotgrid" x="0" y="0" width={GRID} height={GRID} patternUnits="userSpaceOnUse">
        <circle cx="0.5" cy="0.5" r="0.7" fill="#1e2740" />
      </pattern>
      <marker id="arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
        <path d="M0,0 L10,5 L0,10 z" fill="#7eb3f5" />
      </marker>
    </defs>
  );
}

// Cubic-bezier edge between two nodes (left-edge → right-edge).
// `vertical` (one step per row): straight down from a's bottom to b's top.
function edgePath(a, b, vertical) {
  if (vertical) {
    const x1 = a.x + NODE_W / 2, y1 = a.y + NODE_H;
    const x2 = b.x + NODE_W / 2, y2 = b.y;
    if (Math.abs(x2 - x1) < 1) return `M ${x1} ${y1} V ${y2}`;
    const dy = Math.max(30, (y2 - y1) * 0.5);
    return `M ${x1} ${y1} C ${x1} ${y1 + dy}, ${x2} ${y2 - dy}, ${x2} ${y2}`;
  }
  // Wrapped edge (b sits on a lower row, to the left): leave a's right side,
  // drop to the gap between the rows, run back left, then enter b from its left.
  if (b.y > a.y + 1 && b.x <= a.x) {
    const ax = a.x + NODE_W, ay = a.y + NODE_H / 2;
    const bx = b.x, by = b.y + NODE_H / 2;
    const midY = a.y + NODE_H + (ROW_GAP - NODE_H) / 2;
    return `M ${ax} ${ay} H ${ax + 26} V ${midY} H ${bx - 26} V ${by} H ${bx}`;
  }
  const x1 = a.x + NODE_W;
  const y1 = a.y + NODE_H / 2;
  const x2 = b.x;
  const y2 = b.y + NODE_H / 2;
  const dx = Math.max(40, (x2 - x1) * 0.5);
  return `M ${x1} ${y1} C ${x1 + dx} ${y1}, ${x2 - dx} ${y2}, ${x2} ${y2}`;
}

// ── Node hover tooltip ──────────────────────────────────────────────────
// Floating metadata popover for a step card: what the block does, plus the
// category it belongs to with its color-coded swatch (the standalone
// category legend that used to sit atop the canvas was removed in favor of
// surfacing that same mapping here, per-node, on demand).
function StepTooltip({ step, anchorRect, runInfo, besideRight = false }) {
  const meta = STEP_TYPES[step.type] || {};
  const cat = CATEGORIES[meta.cat];
  const accent = stepAccent(step);
  // Category only — no sub-category (e.g. Feature Engineering's "Outlier
  // Handling"). The color-coded mapping this badge exists to show is by
  // top-level category alone, consistently for every block.
  const catLabel = cat?.label;

  const GAP = 10;
  const WIDTH = 260;
  const [rendered, setRendered] = React.useState(null);
  const elRef = React.useRef(null);

  // Measure the tooltip's own height once rendered so we can flip it above
  // vs. below the node depending on available viewport space.
  React.useLayoutEffect(() => {
    if (!elRef.current) return;
    setRendered({ h: elRef.current.offsetHeight });
  }, [step.id]);

  const vh = typeof window !== 'undefined' ? window.innerHeight : 800;
  const vw = typeof window !== 'undefined' ? window.innerWidth : 1200;
  const h = rendered?.h ?? 120;
  const spaceAbove = anchorRect.top;
  const placeBelow = spaceAbove < h + GAP && (anchorRect.bottom + h + GAP) < vh;
  let top = placeBelow ? anchorRect.bottom + GAP : anchorRect.top - h - GAP;
  let left = anchorRect.left + anchorRect.width / 2 - WIDTH / 2;
  left = Math.max(8, Math.min(left, vw - WIDTH - 8));
  // One step per row: above/below would cover the neighbouring steps in the
  // column, so sit to the right of the card (left of it when there's no room).
  if (besideRight) {
    left = anchorRect.right + GAP;
    if (left + WIDTH > vw - 8) left = Math.max(8, anchorRect.left - GAP - WIDTH);
    top = Math.max(8, Math.min(anchorRect.top, vh - h - 8));
  }

  return createPortal(
    <div
      ref={elRef}
      style={{
        position: 'fixed', left, top, width: WIDTH, zIndex: 1000,
        pointerEvents: 'none',
        background: C.surface, border: `1px solid ${C.borderStrong}`,
        borderRadius: 10, padding: '10px 12px',
        boxShadow: '0 8px 24px rgba(0,0,0,.35)',
        fontFamily: 'Inter, sans-serif',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <span style={{ display: 'inline-flex', color: accent }}><StepIcon name={meta.icon} size={16} /></span>
        <span style={{ color: C.fg1, fontWeight: 700, fontSize: '.82rem' }}>{meta.label || step.type}</span>
      </div>
      <div style={{ color: C.fg3, fontSize: '.76rem', lineHeight: 1.4, marginBottom: 8 }}>
        {meta.shortDesc || 'No description available for this step.'}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        {catLabel && (
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            padding: '2px 7px', borderRadius: 5,
            background: accent + '22', border: `1px solid ${accent}`,
          }}>
            <span style={{ width: 8, height: 8, borderRadius: 2, background: accent, display: 'inline-block' }} />
            <span style={{
              color: accent, fontSize: '.66rem', fontWeight: 700,
              textTransform: 'uppercase', letterSpacing: '.05em',
            }}>{catLabel}</span>
          </div>
        )}
        {runInfo && (runInfo.inputCount != null || runInfo.outputCount != null) && (
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            padding: '2px 7px', borderRadius: 5,
            background: C.bar, border: `1px solid ${C.borderStrong}`,
            fontSize: '.7rem', fontFamily: "'JetBrains Mono', monospace",
            color: C.fg2,
          }}>
            <span style={{ color: C.fg4 }}>IN</span>
            <span>{runInfo.inputCount ?? '—'}</span>
            <span style={{ color: C.fg5 }}>→</span>
            <span style={{ color: C.fg4 }}>OUT</span>
            <span style={{
              color: runInfo.delta < 0 ? C.warn : (runInfo.delta > 0 ? C.ok : C.fg2),
            }}>{runInfo.outputCount ?? '—'}</span>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

// ── Problem popup ───────────────────────────────────────────────────────
// Shown while hovering a card's red/amber "!" badge: the step's error, or its
// warnings (message + suggested fix), next to the badge. Portaled for the
// same reasons as StepTooltip.
const PROBLEM_LIST_MAX = 5;

function ProblemTooltip({ step, anchorRect, runInfo }) {
  const GAP = 8;
  const WIDTH = 320;
  const elRef = React.useRef(null);
  const [h, setH] = React.useState(null);
  React.useLayoutEffect(() => {
    if (elRef.current) setH(elRef.current.offsetHeight);
  }, [step.id, runInfo]);

  const error = runInfo?.error;
  const warnings = error ? [] : (runInfo?.warnings || []);
  const shown = warnings.slice(0, PROBLEM_LIST_MAX);
  const more = warnings.length - shown.length;
  const tone = error ? C.error : C.warn;

  const vh = window.innerHeight, vw = window.innerWidth;
  // To the right of the badge; flip to its left when there's no room.
  let left = anchorRect.right + GAP;
  if (left + WIDTH > vw - 8) left = Math.max(8, anchorRect.left - GAP - WIDTH);
  let top = anchorRect.top - 6;
  top = Math.max(8, Math.min(top, vh - (h ?? 160) - 8));

  return createPortal(
    <div ref={elRef} role="tooltip" style={{
      position: 'fixed', left, top, width: WIDTH, zIndex: 1001,
      pointerEvents: 'none',
      background: C.surface, border: `1px solid ${tone}`, borderLeft: `3px solid ${tone}`,
      borderRadius: 10, padding: '10px 12px',
      boxShadow: '0 8px 24px rgba(0,0,0,.35)',
      fontFamily: 'Inter, sans-serif',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6, color: tone, fontWeight: 700, fontSize: '.78rem' }}>
        {error ? <XCircle size={14} /> : <AlertTriangle size={14} />}
        {error ? 'This step failed' : `${warnings.length} warning${warnings.length === 1 ? '' : 's'}`}
      </div>
      {error ? (
        <div style={{ color: C.fg2, fontSize: '.76rem', lineHeight: 1.45, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{error}</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {shown.map((w, i) => (
            <div key={i} style={{ fontSize: '.76rem', lineHeight: 1.45, wordBreak: 'break-word' }}>
              <div style={{ color: C.fg2 }}>
                {w.column && <span style={{ fontFamily: "'JetBrains Mono', monospace", color: C.fg1 }}>{w.column}: </span>}
                {w.message}
              </div>
              {w.suggested_fix && <div style={{ color: C.fg4, marginTop: 2 }}>Fix: {w.suggested_fix}</div>}
            </div>
          ))}
          {more > 0 && (
            <div style={{ color: C.fg4, fontSize: '.72rem' }}>+{more} more — open the step's Debug tab to see all.</div>
          )}
        </div>
      )}
    </div>,
    document.body
  );
}

// ── A single node card ──────────────────────────────────────────────────
// ── Column-focus mark: ring + label on a block that touches the picked column ─
// Reads are ringed in the accent colour, writes in the warning colour, and a
// block that does both gets both rings. The label says it in words so the
// colours are never the only cue.
const ROLE_LABEL = { r: 'reads', w: 'writes', rw: 'reads + writes' };

function ColumnRoleMark({ step, role, current }) {
  const reads = role === 'r' || role === 'rw';
  const writes = role === 'w' || role === 'rw';
  const label = ROLE_LABEL[role] || role;
  const w = label.length * 6.4 + 16;
  const ring = (inset, color, width) => (
    <rect x={step.x - inset} y={step.y - inset} width={NODE_W + inset * 2} height={NODE_H + inset * 2} rx={10 + inset}
      fill="none" stroke={color} strokeWidth={width} pointerEvents="none" />
  );
  return (
    <g pointerEvents="none">
      {writes && ring(reads && writes ? 9 : 6, C.warn, current ? 4 : 3)}
      {reads && ring(5, C.accentStrong, current ? 4 : 3)}
      <g transform={`translate(${step.x}, ${step.y - 28})`}>
        <rect width={w} height={19} rx={9.5} fill={writes && !reads ? C.warn : C.accentStrong} />
        <text x={w / 2} y={13} textAnchor="middle" fontSize={11} fontWeight={700} fill="#fff" fontFamily="Inter, sans-serif">{label}</text>
      </g>
    </g>
  );
}

export function FlowNode({ step, selected, runInfo, onSelect, onOpen, density, isNewborn, onHoverStep, onLeaveStep, onHoverProblem, vertical = false }) {
  const meta = STEP_TYPES[step.type] || {};
  const accent = stepAccent(step);
  const isRunHot = runInfo?.delta < 0;
  const summary = summarizeStep(step);

  // When a step is newly inserted we play a slow, attention-grabbing entrance:
  // the card scales up from ~0.55 to 1.0 inside its own group (so the outer
  // translate isn't clobbered by the CSS transform), and two halo rings
  // ripple outward around the slot to draw the user's eye to it.
  return (
    <g
      transform={`translate(${step.x}, ${step.y})`}
      style={{ cursor: 'pointer' }}
      onClick={(e) => { e.stopPropagation(); onSelect?.(step.id); }}
      onDoubleClick={onOpen ? (e) => { e.stopPropagation(); onOpen(step.id); } : undefined}
      onMouseEnter={(e) => onHoverStep?.(step.id, e.currentTarget.getBoundingClientRect())}
      onMouseLeave={() => onLeaveStep?.(step.id)}
    >
      {/* Spawn halo — rendered as siblings (not inside the scaled group) so
          their ripple scale is independent of the card's spawn scale. */}
      {isNewborn && (
        <g style={{ pointerEvents: 'none' }}>
          <rect
            x={-8} y={-8} width={NODE_W + 16} height={NODE_H + 16}
            rx={16} ry={16}
            fill="none"
            stroke={accent}
            strokeWidth={2}
            style={{
              transformBox: 'fill-box',
              transformOrigin: 'center',
              animation: 'omFlowNodeHalo 1400ms cubic-bezier(.2,.7,.2,1) both',
            }}
          />
          <rect
            x={-8} y={-8} width={NODE_W + 16} height={NODE_H + 16}
            rx={16} ry={16}
            fill="none"
            stroke={accent}
            strokeWidth={1.5}
            style={{
              transformBox: 'fill-box',
              transformOrigin: 'center',
              animation: 'omFlowNodeHalo 1400ms cubic-bezier(.2,.7,.2,1) 280ms both',
            }}
          />
        </g>
      )}

      {/* Inner group holds the card itself; spawn scale lives here so the
          outer translate transform attribute isn't overridden. */}
      <g style={{
        opacity: step.disabled ? 0.4 : 1,
        transformBox: 'fill-box',
        transformOrigin: 'center',
        animation: isNewborn ? 'omFlowNodeSpawn 900ms cubic-bezier(.2,1.05,.4,1) both' : undefined,
      }}>
        {/* Selected halo */}
        {selected && (
          <rect
            x={-3} y={-3} width={NODE_W + 6} height={NODE_H + 6}
            rx={13} ry={13}
            fill="none"
            stroke={C.accent} strokeWidth={1.5}
            opacity={0.9}
            strokeDasharray="3 3"
          />
        )}

        {/* Card body */}
        <rect
          x={0} y={0} width={NODE_W} height={NODE_H}
          rx={10} ry={10}
          fill={C.surface}
          stroke={selected ? C.accent : C.borderStrong}
          strokeWidth={selected ? 1.5 : 1}
        />

        {/* Left accent bar (by step type) */}
        <rect x={0} y={0} width={4} height={NODE_H} rx={10} fill={accent} opacity={0.85} />

        {/* Type icon */}
        <foreignObject x={16} y={14} width={32} height={32}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, color: accent }}>
            <StepIcon name={meta.icon} size={20} />
          </div>
        </foreignObject>

        {/* Title + summary */}
        <foreignObject x={54} y={13} width={NODE_W - 68} height={NODE_H - 20}>
          <div style={{
            fontFamily: 'Inter, sans-serif',
            color: C.fg1, fontSize: '.85rem', fontWeight: 700,
            letterSpacing: '.03em', textTransform: 'uppercase',
            marginBottom: 4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}>{meta.label}</div>
          <div style={{
            fontFamily: 'Inter, sans-serif',
            color: C.fg3, fontSize: '.82rem',
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            marginBottom: 4,
          }}>{summary}</div>
        </foreignObject>

        {/* Problem badge — red when the step failed, amber when it carries warnings */}
        {(runInfo?.error || runInfo?.warnings?.length > 0) && (
          <g
            style={{ cursor: 'help' }}
            onMouseEnter={(e) => onHoverProblem?.(step.id, e.currentTarget.getBoundingClientRect())}
            onMouseLeave={() => onHoverProblem?.(step.id, null)}
          >
            {/* Invisible, slightly larger hit area so the small badge is easy to hover */}
            <circle cx={NODE_W - 10} cy={10} r={12} fill="transparent" />
            <circle cx={NODE_W - 10} cy={10} r={8} fill={runInfo.error ? C.error : C.warn} pointerEvents="none" />
            <text x={NODE_W - 10} y={14} textAnchor="middle" fontSize={11} fontWeight={700} fill="#fff" pointerEvents="none">!</text>
          </g>
        )}

        {/* In/out ports */}
        {!step.type.startsWith('source') && (
          <circle cx={vertical ? NODE_W / 2 : 0} cy={vertical ? 0 : NODE_H / 2} r={4.5} fill={C.bar} stroke={accent} strokeWidth={1.5} />
        )}
        {step.type !== 'output' && (
          <circle cx={vertical ? NODE_W / 2 : NODE_W} cy={vertical ? NODE_H : NODE_H / 2} r={4.5} fill={C.bar} stroke={accent} strokeWidth={1.5} />
        )}
      </g>
    </g>
  );
}

// ── Steps-per-row setting (canvas control pane) ─────────────────────────
// A whole number MIN..MAX. Typing is kept as a local draft so "1" on the way
// to "12" doesn't re-flow the canvas; it commits on Enter or blur, and an
// invalid entry snaps back to the current value.
function StepsPerRowField({ value, onChange }) {
  const [draft, setDraft] = React.useState(String(value));
  React.useEffect(() => { setDraft(String(value)); }, [value]);
  const commit = () => {
    const n = Number(draft);
    if (Number.isInteger(n) && n >= MIN_STEPS_PER_ROW && n <= MAX_STEPS_PER_ROW) {
      if (n !== value) onChange(n);
    } else {
      setDraft(String(value));
    }
  };
  return (
    <label
      title={`Blocks per row on the canvas before wrapping to the next row (${MIN_STEPS_PER_ROW}–${MAX_STEPS_PER_ROW}). Saved with the pipeline.`}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, alignSelf: 'center', paddingLeft: 4 }}
    >
      <span style={{ color: C.fg4, fontSize: '.72rem', whiteSpace: 'nowrap' }}>steps/row</span>
      <input
        type="number" inputMode="numeric"
        min={MIN_STEPS_PER_ROW} max={MAX_STEPS_PER_ROW} step={1}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); commit(); e.currentTarget.blur(); }
          else if (e.key === 'Escape') { setDraft(String(value)); e.currentTarget.blur(); }
        }}
        style={{
          width: 44, padding: '3px 6px', borderRadius: 6,
          background: C.input, color: C.fg2, border: `1px solid ${C.borderStrong}`,
          fontFamily: "'JetBrains Mono', monospace", fontSize: '.78rem',
        }}
      />
    </label>
  );
}

// ── Main canvas ─────────────────────────────────────────────────────────
export function FlowCanvas({ pipeline, runMap, selectedId, onSelect, onOpenStep, stepsPerRow = DEFAULT_STEPS_PER_ROW, onStepsPerRowChange, density = 'normal', isFullscreen, onToggleFullscreen, lastAddedStepId, onDropStepType, onReorderStep, canUndo, canRedo, onUndo, onRedo, columnFocus = null, onClearColumnFocus }) {
  // Block positions come from pipeline order alone (stepsPerRow blocks per
  // row, wrapping to the next row), so long pipelines never need a long
  // sideways scroll and older saved x/y values are simply superseded.
  const laid = React.useMemo(() => layoutGrid(pipeline.steps, stepsPerRow), [pipeline.steps, stepsPerRow]);
  // One step per row: the flow runs top to bottom (arrows and ports vertical).
  const vertical = stepsPerRow === 1;
  const containerRef = React.useRef(null);
  const [pan, setPan] = React.useState({ x: -40, y: -40 });
  const [zoom, setZoom] = React.useState(0.85);
  const [dragging, setDragging] = React.useState(false);
  const dragStart = React.useRef(null);
  // { id, rect } of the node currently under the cursor, or null. `rect` is
  // the node's own getBoundingClientRect() at hover time — using the real
  // rendered box (rather than recomputing from step.x/y/pan/zoom) means the
  // tooltip anchor stays correct regardless of current pan/zoom.
  const [hoverInfo, setHoverInfo] = React.useState(null);
  const onHoverStep = React.useCallback((id, rect) => setHoverInfo({ id, rect }), []);
  const onLeaveStep = React.useCallback((id) => {
    setHoverInfo(cur => (cur?.id === id ? null : cur));
  }, []);
  // Hovering a card's problem badge swaps the card tooltip for the warning
  // popup (`problemRect` set); leaving the badge but staying on the card
  // brings the card tooltip back.
  const onHoverProblem = React.useCallback((id, rect) => {
    setHoverInfo(cur => (cur?.id === id ? { ...cur, problemRect: rect } : cur));
  }, []);
  // Mirrors `pan` synchronously so animatePanTo can read the current value
  // without relying on a setState-updater callback, whose timing relative to
  // the very next requestAnimationFrame isn't guaranteed (it was previously
  // possible for the tick's first frame to run before React had invoked the
  // updater, reading `undefined.x`).
  const panRef = React.useRef(pan);
  panRef.current = pan;
  const zoomRef = React.useRef(zoom);
  zoomRef.current = zoom;
  // Animate pan from current value to (tx, ty) over `duration` ms.
  const animRef = React.useRef(null);
  const animatePanTo = React.useCallback((tx, ty, duration = 320) => {
    if (animRef.current) cancelAnimationFrame(animRef.current);
    const ease = (t) => 1 - Math.pow(1 - t, 3);
    const startPan = panRef.current;
    const t0 = performance.now();
    const step = (now) => {
      const t = Math.min(1, (now - t0) / duration);
      const k = ease(t);
      setPan({
        x: startPan.x + (tx - startPan.x) * k,
        y: startPan.y + (ty - startPan.y) * k,
      });
      if (t < 1) animRef.current = requestAnimationFrame(step);
      else animRef.current = null;
    };
    animRef.current = requestAnimationFrame(step);
  }, []);

  // ── Animated node positions ────────────────────────────────────────────
  // When `laid` changes (a step inserted/removed), surrounding
  // nodes shift along x to make room / close the gap. Snapping is jarring,
  // so we tween each step's displayed (x,y) from its previous position to
  // its new target over ~900ms. Newborn steps (no previous position) snap to
  // their slot and rely on FlowNode's scale-in + halo "spawn" animation.
  const [displayed, setDisplayed] = React.useState(laid);
  const displayedRef = React.useRef(displayed);
  displayedRef.current = displayed;
  const stepsAnimRef = React.useRef(null);
  const quickAnimRef = React.useRef(false);

  React.useEffect(() => {
    const target = laid;
    const startMap = new Map(displayedRef.current.map(s => [s.id, { x: s.x, y: s.y }]));
    const positionsMatch = target.length === displayedRef.current.length &&
      target.every(s => {
        const p = startMap.get(s.id);
        return p && p.x === s.x && p.y === s.y;
      });
    if (positionsMatch) {
      // Contents may have changed (config edits, run state) but positions/IDs
      // are stable — sync without animating.
      setDisplayed(target);
      return;
    }

    if (stepsAnimRef.current) cancelAnimationFrame(stepsAnimRef.current);
    const t0 = performance.now();
    const dur = quickAnimRef.current ? 350 : 900;
    quickAnimRef.current = false;
    const easeOut = (t) => 1 - Math.pow(1 - t, 3);
    const tick = (now) => {
      const t = Math.min(1, (now - t0) / dur);
      const k = easeOut(t);
      const next = target.map(s => {
        const prev = startMap.get(s.id);
        if (!prev) return s; // newborn — snap to slot, spawn anim in FlowNode
        return { ...s, x: prev.x + (s.x - prev.x) * k, y: prev.y + (s.y - prev.y) * k };
      });
      setDisplayed(next);
      if (t < 1) stepsAnimRef.current = requestAnimationFrame(tick);
      else stepsAnimRef.current = null;
    };
    stepsAnimRef.current = requestAnimationFrame(tick);
    return () => {
      if (stepsAnimRef.current) cancelAnimationFrame(stepsAnimRef.current);
    };
  }, [laid]);

  // Auto-fit to container on mount + when steps change significantly — but
  // only until the user zooms or pans themselves. After that the view is
  // theirs: a container resize (inspector/preview toggling, deselecting a
  // step) or a step added/removed never resets it. Only an explicit reset
  // (clicking the zoom percentage) fits again.
  const userViewRef = React.useRef(false);
  const fitRef = React.useRef(null);
  const laidRef = React.useRef(laid);
  laidRef.current = laid;
  const resetView = () => {
    userViewRef.current = false;
    pendingViewRef.current = null;
    writeCanvasView(pipeline.id, null);
    fitRef.current?.();
  };
  React.useEffect(() => {
    if (!containerRef.current) return;
    const fit = () => {
      if (userViewRef.current) return;
      const el = containerRef.current; if (!el) return;
      const w = el.clientWidth, h = el.clientHeight;
      if (!w || !h) return;
      const xs = laidRef.current.map(s => s.x);
      const ys = laidRef.current.map(s => s.y);
      const minX = Math.min(...xs) - 40;
      const maxX = Math.max(...xs) + NODE_W + 40;
      const minY = Math.min(...ys) - 40;
      const maxY = Math.max(...ys) + NODE_H + 40;
      const contentW = maxX - minX;
      const contentH = maxY - minY;
      const z = Math.min(1.0, Math.max(0.45, Math.min(w / contentW, h / contentH)));
      setZoom(z);
      // center
      const cx = (w - contentW * z) / 2;
      const cy = (h - contentH * z) / 2;
      setPan({ x: cx - minX * z, y: cy - minY * z });
    };
    fitRef.current = fit;
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, [laid.length]);

  // Remember the view per pipeline once the user has zoomed or panned, and
  // bring it back when that pipeline is opened again (also after a reload).
  // Writes are debounced; a pending write is flushed before switching to
  // another pipeline, on unmount and before the page unloads.
  const pendingViewRef = React.useRef(null);
  const restoringRef = React.useRef(false);
  const flushView = React.useCallback(() => {
    const p = pendingViewRef.current;
    if (!p) return;
    clearTimeout(p.timer);
    pendingViewRef.current = null;
    writeCanvasView(p.id, p.view);
  }, []);
  React.useLayoutEffect(() => {
    flushView();
    const saved = readCanvasView(pipeline.id);
    restoringRef.current = true;
    if (saved) {
      userViewRef.current = true;
      if (animRef.current) { cancelAnimationFrame(animRef.current); animRef.current = null; }
      setZoom(saved.zoom);
      setPan(saved.pan);
    } else {
      userViewRef.current = false;
      fitRef.current?.();
    }
  }, [pipeline.id, flushView]);
  React.useEffect(() => {
    // Skip the commit where the previous pipeline's view is still in state.
    if (restoringRef.current) { restoringRef.current = false; return; }
    if (!userViewRef.current) return;
    if (pendingViewRef.current) clearTimeout(pendingViewRef.current.timer);
    const id = pipeline.id;
    const view = { zoom, pan };
    pendingViewRef.current = { id, view, timer: setTimeout(() => {
      pendingViewRef.current = null;
      writeCanvasView(id, view);
    }, 300) };
  }, [zoom, pan, pipeline.id]);
  React.useEffect(() => {
    window.addEventListener('beforeunload', flushView);
    return () => {
      window.removeEventListener('beforeunload', flushView);
      flushView();
    };
  }, [flushView]);

  // True when the whole card is inside the canvas viewport at the current
  // pan/zoom — such a step never needs to be scrolled to.
  const isFullyVisible = React.useCallback((st) => {
    const el = containerRef.current;
    if (!el || !st) return false;
    const z = zoomRef.current, p = panRef.current;
    const nx1 = st.x * z + p.x, ny1 = st.y * z + p.y;
    return nx1 >= 0 && ny1 >= 0
      && nx1 + NODE_W * z <= el.clientWidth && ny1 + NODE_H * z <= el.clientHeight;
  }, []);

  // When `selectedId` changes (e.g. via outline rail click), make sure the
  // node is visible in the viewport. We pan with a smooth ease-out tween;
  // a node that is already fully visible is left alone so canvas clicks
  // don’t cause the world to jitter.
  React.useEffect(() => {
    if (!selectedId || !containerRef.current) return;
    const step = laid.find(s => s.id === selectedId);
    if (!step) return;
    const el = containerRef.current;
    const w = el.clientWidth, h = el.clientHeight;
    if (!w || !h) return;
    if (isFullyVisible(step)) return;
    const cx = (step.x + NODE_W / 2) * zoom;
    const cy = (step.y + NODE_H / 2) * zoom;
    animatePanTo(w / 2 - cx, h / 2 - cy);
     
  }, [selectedId]);

  // Bounds panning on both axes so the content can't be scrolled/dragged
  // away into empty space forever — once the leftmost node (always the
  // source block) or rightmost node (always the output block) edge, plus a
  // little breathing room, reaches the viewport's edge, panning further in
  // that direction is clamped; same idea vertically for the topmost/
  // bottommost node.
  const clampPan = React.useCallback((x, y, z) => {
    const el = containerRef.current;
    const w = el ? el.clientWidth : 0;
    const h = el ? el.clientHeight : 0;
    if (!laid.length) return { x, y };
    const xs = laid.map(s => s.x);
    const ys = laid.map(s => s.y);
    const minX = Math.min(...xs) - 40, maxX = Math.max(...xs) + NODE_W + 40;
    const minY = Math.min(...ys) - 40, maxY = Math.max(...ys) + NODE_H + 40;
    // For each axis: the two positions at which content's near/far edge
    // touches the viewport's far/near edge — which one is actually the
    // lower vs. upper bound flips depending on whether the content is
    // bigger or smaller than the viewport on that axis, so sort rather
    // than assume.
    let cx = x, cy = y;
    if (w) {
      const a = w - maxX * z, b = -minX * z;
      const lo = Math.min(a, b), hi = Math.max(a, b);
      cx = Math.max(lo, Math.min(hi, x));
    }
    if (h) {
      const a = h - maxY * z, b = -minY * z;
      const lo = Math.min(a, b), hi = Math.max(a, b);
      cy = Math.max(lo, Math.min(hi, y));
    }
    return { x: cx, y: cy };
  }, [laid]);

  // ── Find-on-canvas (Ctrl/Cmd+F while the pointer is over the canvas) ──
  // Case-insensitive substring match over each step's label, its block type
  // name and its one-line summary. Matches are listed in pipeline order;
  // Enter / Shift+Enter (or the arrows) step through them, panning the
  // canvas to centre the current match. Non-matching cards are dimmed.
  const [searchOpen, setSearchOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const [matchIdx, setMatchIdx] = React.useState(0);
  const searchInputRef = React.useRef(null);
  const pointerOverRef = React.useRef(false);
  const q = query.trim().toLowerCase();
  const matches = React.useMemo(() => {
    if (!q) return [];
    return pipelineOrder(laid).filter(st => {
      const meta = STEP_TYPES[st.type] || {};
      const hay = `${st.label || ''} ${meta.label || ''} ${st.type} ${summarizeStep(st) || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [q, laid]);
  const matchIds = React.useMemo(() => new Set(matches.map(m => m.id)), [matches]);
  const currentMatch = matches.length ? matches[Math.min(matchIdx, matches.length - 1)] : null;

  const centerOn = React.useCallback((st) => {
    const el = containerRef.current;
    if (!el || !st || isFullyVisible(st)) return;
    const z = zoomRef.current;
    const t = clampPan(el.clientWidth / 2 - (st.x + NODE_W / 2) * z, el.clientHeight / 2 - (st.y + NODE_H / 2) * z, z);
    animatePanTo(t.x, t.y);
  }, [animatePanTo, clampPan, isFullyVisible]);

  // Pan the newly added block into view the moment it spawns — covers both
  // a palette click (inserted to the left of a selected step that may
  // already sit at the edge of the viewport) and a palette drag-drop onto
  // an insertion point that isn't currently visible.
  React.useEffect(() => {
    if (!lastAddedStepId) return;
    const node = laid.find(st => st.id === lastAddedStepId);
    if (node) centerOn(node);
  }, [lastAddedStepId]);

  const goToMatch = (idx) => {
    if (!matches.length) return;
    const i = (idx + matches.length) % matches.length;
    setMatchIdx(i);
    centerOn(matches[i]);
  };
  const closeSearch = () => { setSearchOpen(false); setQuery(''); setMatchIdx(0); };

  React.useEffect(() => { setMatchIdx(0); if (matches[0]) centerOn(matches[0]); }, [q]);

  // Column focus (Data Preview → click a column): every block that reads or
  // writes the picked column is ringed + labelled, the rest dim. The canvas
  // scrolls to the first such block in pipeline order and selects it, so the
  // Inspector follows along; ◀ ▶ step through the rest, moving the selection
  // (and so the Inspector) to each one in turn.
  const [focusIdx, setFocusIdx] = React.useState(0);
  const focusKey = columnFocus?.key;
  const focusOrder = columnFocus?.order || [];
  const goToFocus = (idx) => {
    if (!focusOrder.length) return;
    const i = (idx + focusOrder.length) % focusOrder.length;
    setFocusIdx(i);
    centerOn(laid.find(st => st.id === focusOrder[i]));
    onSelect?.(focusOrder[i]);
  };
  React.useEffect(() => {
    setFocusIdx(0);
    if (focusKey && focusOrder.length) {
      centerOn(laid.find(st => st.id === focusOrder[0]));
      onSelect?.(focusOrder[0]);
    }
  }, [focusKey]);
  React.useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && !e.shiftKey && !e.altKey && e.key.toLowerCase() === 'f') {
        const inCanvas = pointerOverRef.current || containerRef.current?.contains(document.activeElement);
        if (!inCanvas) return;   // elsewhere: leave the browser's own find alone
        e.preventDefault();
        setSearchOpen(true);
        setTimeout(() => { searchInputRef.current?.focus(); searchInputRef.current?.select(); }, 0);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);


  // ── Drag to reorder / drop from palette ────────────────────────────────
  // `nodeDrag` = { id, dx, dy } (world-space offset of the dragged card) once
  // the pointer has moved past a small threshold; `dropAfterId` is the step
  // the dragged/dropped block would be inserted after (drives the gap marker).
  const [nodeDrag, setNodeDrag] = React.useState(null);
  const nodeDragRef = React.useRef(null);
  nodeDragRef.current = nodeDrag;
  const [dropAfterId, setDropAfterId] = React.useState(null);
  const nodePress = React.useRef(null);
  const suppressClick = React.useRef(false);
  const pointerRef = React.useRef({ x: 0, y: 0 });
  const dragApiRef = React.useRef(null);

  // Step a block centred at `worldCx` should follow: the last non-output step
  // (other than `excludeId`) whose centre is left of it; the source otherwise.
  // Rows are compared first (the pointer's row, by its y), then x within it.
  const slotAfter = (worldCx, worldCy, excludeId) => {
    const list = pipelineOrder(laid).filter(s => s.id !== excludeId && s.type !== 'output');
    const y0 = laid.length ? Math.min(...laid.map(s => s.y)) : 0;
    const rowOf = (y) => Math.max(0, Math.round((y - y0) / rowGapFor(stepsPerRow)));
    const key = (cx, cy) => rowOf(cy - NODE_H / 2) * 1e7 + cx;
    const here = key(worldCx, worldCy);
    let after = list[0];
    for (const s of list) if (key(s.x + NODE_W / 2, s.y + NODE_H / 2) < here) after = s;
    return after;
  };
  const toWorldX = (clientX) => {
    const rect = containerRef.current.getBoundingClientRect();
    return (clientX - rect.left - panRef.current.x) / zoomRef.current;
  };
  const toWorldY = (clientY) => {
    const rect = containerRef.current.getBoundingClientRect();
    return (clientY - rect.top - panRef.current.y) / zoomRef.current;
  };

  const onNodeMouseDown = (e, step) => {
    if (e.button !== 0 || step.type === 'source' || step.type === 'output') return;
    // wx/wy: the pointer's WORLD position at press time. The drag offset is
    // measured against it (not against the screen-space delta), so panning
    // the canvas mid-drag (edge auto-scroll) keeps the card under the pointer.
    nodePress.current = {
      id: step.id, cx: e.clientX, cy: e.clientY, moved: false,
      wx: toWorldX(e.clientX), wy: toWorldY(e.clientY),
    };
    pointerRef.current = { x: e.clientX, y: e.clientY };
  };
  // Move the dragged card to the pointer and refresh the drop marker.
  const updateNodeDrag = (clientX, clientY) => {
    const press = nodePress.current;
    if (!press) return;
    pointerRef.current = { x: clientX, y: clientY };
    if (!press.moved && Math.hypot(clientX - press.cx, clientY - press.cy) < 5) return;
    press.moved = true;
    setHoverInfo(null);
    const dx = toWorldX(clientX) - press.wx;
    const dy = toWorldY(clientY) - press.wy;
    setNodeDrag({ id: press.id, dx, dy });
    const st = laid.find(s => s.id === press.id);
    if (st) setDropAfterId(slotAfter(st.x + dx + NODE_W / 2, st.y + dy + NODE_H / 2, press.id)?.id ?? null);
  };
  const onMouseDown = (e) => {
    if (e.target.closest('g[data-node]')) return;
    setDragging(true);
    setHoverInfo(null);
    dragStart.current = { x: e.clientX, y: e.clientY, panX: pan.x, panY: pan.y };
  };
  const onMouseMove = (e) => {
    const press = nodePress.current;
    if (press) {
      updateNodeDrag(e.clientX, e.clientY);
      return;
    }
    if (!dragging || !dragStart.current) return;
    const dx = e.clientX - dragStart.current.x;
    const dy = e.clientY - dragStart.current.y;
    if (dx || dy) userViewRef.current = true;
    setPan(clampPan(dragStart.current.panX + dx, dragStart.current.panY + dy, zoomRef.current));
  };
  const endNodeDrag = (commit) => {
    const press = nodePress.current;
    nodePress.current = null;
    if (press?.moved) {
      suppressClick.current = true;
      setTimeout(() => { suppressClick.current = false; }, 50);
      if (commit && dropAfterId) {
        // Start the slide from where the card was dropped (not its old slot),
        // so it settles into place while the others shift aside.
        const d = nodeDragRef.current;
        if (d) {
          setDisplayed(cur => cur.map(s => (s.id === press.id ? { ...s, x: s.x + d.dx, y: s.y + d.dy } : s)));
        }
        quickAnimRef.current = true;
        onReorderStep?.(press.id, dropAfterId);
      }
    }
    setNodeDrag(null);
    setDropAfterId(null);
  };

  // Edge auto-scroll + window-level tracking while a card is being dragged:
  // when the pointer nears (or passes) the canvas edge the view pans that way
  // every frame, and the card keeps following the pointer. Releasing outside
  // the canvas cancels, as before.
  const cardDragging = !!nodeDrag;
  dragApiRef.current = { updateNodeDrag, endNodeDrag, clampPan };
  React.useEffect(() => {
    if (!cardDragging) return undefined;
    const EDGE = 70, MAX_SPEED = 22;
    const inside = (x, y) => {
      const r = containerRef.current?.getBoundingClientRect();
      return !!r && x >= r.left && x <= r.right && y >= r.top && y <= r.bottom;
    };
    const onMove = (e) => dragApiRef.current.updateNodeDrag(e.clientX, e.clientY);
    const onUp = (e) => {
      if (!nodePress.current) return;
      dragApiRef.current.endNodeDrag(inside(e.clientX, e.clientY));
    };
    const speed = (pos, lo, hi) => {
      if (pos < lo + EDGE) return -Math.min(1, (lo + EDGE - pos) / EDGE) * MAX_SPEED;
      if (pos > hi - EDGE) return Math.min(1, (pos - (hi - EDGE)) / EDGE) * MAX_SPEED;
      return 0;
    };
    let raf = 0;
    const tick = () => {
      const el = containerRef.current;
      if (el && nodePress.current?.moved) {
        const r = el.getBoundingClientRect();
        const { x, y } = pointerRef.current;
        const vx = speed(x, r.left, r.right);
        const vy = speed(y, r.top, r.bottom);
        if (vx || vy) {
          const cur = panRef.current;
          const next = dragApiRef.current.clampPan(cur.x - vx, cur.y - vy, zoomRef.current);
          if (next.x !== cur.x || next.y !== cur.y) {
            panRef.current = next;
            setPan(next);
            dragApiRef.current.updateNodeDrag(x, y);
          }
        }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [cardDragging]);
  const onMouseUp = () => {
    if (nodePress.current) { endNodeDrag(true); return; }
    setDragging(false); dragStart.current = null;
  };
  const onMouseLeave = () => {
    pointerOverRef.current = false;
    // A card that is mid-drag keeps going past the canvas edge (window-level
    // listeners below finish it); only an un-started press is dropped here.
    if (nodePress.current) { if (!nodePress.current.moved) endNodeDrag(false); return; }
    setDragging(false); dragStart.current = null;
  };

  const hasStepDrag = (e) => Array.from(e.dataTransfer?.types || []).includes(DRAG_MIME);
  const onDragOver = (e) => {
    if (!hasStepDrag(e)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    setDropAfterId(slotAfter(toWorldX(e.clientX), toWorldY(e.clientY), null)?.id ?? null);
  };
  const onDragLeave = (e) => {
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setDropAfterId(null);
  };
  const onDrop = (e) => {
    if (!hasStepDrag(e)) return;
    e.preventDefault();
    const type = e.dataTransfer.getData(DRAG_MIME);
    const after = slotAfter(toWorldX(e.clientX), toWorldY(e.clientY), null);
    setDropAfterId(null);
    if (type && after) onDropStepType?.(type, after.id);
  };

  // Wheel handling is attached as a native, non-passive listener (see the
  // effect below) rather than React's onWheel — React's synthetic wheel
  // handler is registered passively under the hood, so calling
  // e.preventDefault() there is unreliable and mouse-wheel scrolling used
  // to leak through to the outer page (scrolling/zooming the whole
  // document) instead of staying scoped to the canvas viewport.
  // Ctrl/Cmd+wheel (and trackpad pinch, which browsers report as
  // ctrlKey+wheel) zooms centered on the cursor; plain wheel pans.
  React.useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const handler = (e) => {
      e.preventDefault();
      setHoverInfo(null);
      userViewRef.current = true;
      const rect = el.getBoundingClientRect();
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;
      if (e.ctrlKey || e.metaKey) {
        const z0 = zoomRef.current;
        const z1 = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, z0 * Math.exp(-e.deltaY * 0.0018)));
        const p0 = panRef.current;
        const wx = (mx - p0.x) / z0;
        const wy = (my - p0.y) / z0;
        setZoom(z1);
        setPan(clampPan(mx - wx * z1, my - wy * z1, z1));
      } else {
        const p0 = panRef.current;
        // A physical mouse wheel has no horizontal axis of its own — Shift
        // + wheel is the conventional stand-in (same as most apps/browsers).
        // Some browsers already remap this to deltaX with deltaY zeroed;
        // others still report it as plain deltaY even with Shift held, so
        // read whichever axis is actually nonzero rather than assuming one.
        if (e.shiftKey) {
          const dx = e.deltaX || e.deltaY;
          setPan(clampPan(p0.x - dx, p0.y, zoomRef.current));
        } else {
          setPan(clampPan(p0.x - e.deltaX, p0.y - e.deltaY, zoomRef.current));
        }
      }
    };
    el.addEventListener('wheel', handler, { passive: false });
    return () => el.removeEventListener('wheel', handler);
  }, [clampPan]);

  // Build edges from order (use animated `displayed` positions so edges
  // glide with their endpoints as steps shift).
  const ordered = pipelineOrder(displayed);
  const edges = [];
  for (let i = 1; i < ordered.length; i++) {
    edges.push([ordered[i - 1], ordered[i]]);
  }

  // Canvas extents
  const maxX = Math.max(...displayed.map(s => s.x + NODE_W)) + 200;
  const maxY = Math.max(...displayed.map(s => s.y + NODE_H)) + 300;

  return (
    <div
      ref={containerRef}
      onClick={() => { if (!suppressClick.current) onSelect?.(null); }}
      onMouseDown={onMouseDown}
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
      onMouseLeave={onMouseLeave}
      onMouseEnter={() => { pointerOverRef.current = true; }}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      style={{
        position: 'relative', width: '100%', height: '100%',
        overflow: 'hidden', background: C.app,
        cursor: dragging ? 'grabbing' : 'grab',
        touchAction: 'none', overscrollBehavior: 'contain',
      }}
    >
      <svg
        width="100%" height="100%"
        style={{ position: 'absolute', inset: 0, userSelect: 'none' }}
      >
        <CanvasBg />
        {/* background dots covering the whole viewport */}
        <rect width="100%" height="100%" fill="url(#dotgrid)" />

        <g transform={`translate(${pan.x},${pan.y}) scale(${zoom})`}>
          {/* Edges first so nodes layer above */}
          {edges.map(([a, b], i) => (
            <g key={i}>
              <path
                d={edgePath(a, b, vertical)}
                stroke="#7eb3f5"
                strokeOpacity={0.75}
                strokeWidth={1.8}
                fill="none"
                markerEnd="url(#arrow)"
              />
              {/* tiny row-count badge midway */}
              {(() => {
                const info = runMap?.get(a.id);
                const savedRows = a.type === 'source' ? pipeline.inputFile?.rows : null;
                const count = info?.outputCount ?? savedRows;
                if (count == null) return null;
                // One step per row: the pill sits to the right of the
                // vertical arrow, halfway down the gap between the cards.
                if (vertical) {
                  const label = ` ${count} rows `;
                  const w = Math.max(40, label.length * 6 + 10);
                  const vy = (a.y + NODE_H + b.y) / 2;
                  return (
                    <g transform={`translate(${a.x + NODE_W / 2 + 12}, ${vy - 9})`}>
                      <rect width={w} height={18} rx={9} fill={C.bar} stroke={C.borderStrong} />
                      <text x={w / 2} y={12} textAnchor="middle" fill={C.fg3}
                        style={{ fontSize: 10, fontFamily: "'JetBrains Mono', monospace", letterSpacing: '.02em' }}>
                        {label}
                      </text>
                    </g>
                  );
                }
                const wrapped = b.y > a.y + 1 && b.x <= a.x;
                const x1 = a.x + NODE_W, x2 = b.x;
                const mx = wrapped ? (x1 + 26 + x2 - 26) / 2 : (x1 + x2) / 2;
                // Two adjacent same-row nodes are only ~40px apart — far
                // narrower than a wide row-count pill needs — so the pill
                // necessarily reached under the NODE CARDS themselves, not
                // just their small connector dots; cards paint after edges
                // too, so they clipped it regardless of a small vertical
                // nudge, and sitting that close to the dot-to-dot line also
                // visually broke up the connecting arrow. Floating the
                // badge below the row instead — the same spot the wrapped
                // case already uses — clears both nodes entirely (cards
                // only occupy the row's own NODE_H band) and leaves the
                // connecting line/arrow fully visible, unobstructed.
                const y = wrapped
                  ? a.y + NODE_H + (ROW_GAP - NODE_H) / 2
                  : a.y + NODE_H + 20;
                const label = ` ${count} rows `;
                const w = Math.max(40, label.length * 6 + 10);
                return (
                  <g transform={`translate(${mx - w / 2}, ${y - 9})`}>
                    <rect width={w} height={18} rx={9} fill={C.bar} stroke={C.borderStrong} />
                    <text x={w / 2} y={12} textAnchor="middle" fill={C.fg3}
                      style={{ fontSize: 10, fontFamily: "'JetBrains Mono', monospace", letterSpacing: '.02em' }}>
                      {label}
                    </text>
                  </g>
                );
              })()}
            </g>
          ))}

          {/* Insertion marker while dragging a block (palette or reorder) */}
          {dropAfterId && (() => {
            const a = displayed.find(s => s.id === dropAfterId);
            if (!a) return null;
            if (vertical) {
              const y = a.y + NODE_H + (rowGapFor(1) - NODE_H) / 2;
              return (
                <line x1={a.x - 12} x2={a.x + NODE_W + 12} y1={y} y2={y}
                  stroke={C.accentStrong} strokeWidth={3} strokeLinecap="round"
                  strokeDasharray="6 5" pointerEvents="none" />
              );
            }
            const x = a.x + NODE_W + 20;
            return (
              <line x1={x} x2={x} y1={a.y - 12} y2={a.y + NODE_H + 12}
                stroke={C.accentStrong} strokeWidth={3} strokeLinecap="round"
                strokeDasharray="6 5" pointerEvents="none" />
            );
          })()}

          {/* Nodes (the card being dragged is painted last, on top) */}
          {[...displayed].sort((a, b) => (a.id === nodeDrag?.id) - (b.id === nodeDrag?.id)).map(s => (
            <g
              key={s.id}
              data-node="1"
              onMouseDown={(e) => onNodeMouseDown(e, s)}
              transform={nodeDrag?.id === s.id ? `translate(${nodeDrag.dx}, ${nodeDrag.dy})` : undefined}
              opacity={nodeDrag?.id === s.id ? 0.85 : (columnFocus
                ? (columnFocus.highlights.has(s.id) ? 1 : 0.3)
                : (searchOpen && q && !matchIds.has(s.id) ? 0.3 : 1))}
              pointerEvents={nodeDrag?.id === s.id ? 'none' : undefined}
            >
              {columnFocus && columnFocus.highlights.has(s.id) && (
                <ColumnRoleMark step={s} role={columnFocus.highlights.get(s.id)} current={focusOrder[focusIdx] === s.id} />
              )}
              {!columnFocus && searchOpen && q && matchIds.has(s.id) && (
                <rect
                  x={s.x - 7} y={s.y - 7} width={NODE_W + 14} height={NODE_H + 14} rx={14}
                  fill="none" pointerEvents="none"
                  stroke={currentMatch?.id === s.id ? C.warn : C.accentStrong}
                  strokeWidth={currentMatch?.id === s.id ? 3 : 2}
                  strokeDasharray={currentMatch?.id === s.id ? undefined : '5 4'}
                />
              )}
              <FlowNode
                step={s}
                selected={selectedId === s.id}
                runInfo={runMap?.get(s.id)}
                onSelect={(id) => { if (!suppressClick.current) onSelect?.(id); }}
                onOpen={onOpenStep ? (id) => { if (!suppressClick.current) onOpenStep(id); } : undefined}
                density={density}
                isNewborn={lastAddedStepId === s.id}
                onHoverStep={onHoverStep}
                onLeaveStep={onLeaveStep}
                onHoverProblem={onHoverProblem}
                vertical={vertical}
              />
            </g>
          ))}
        </g>
      </svg>

      {/* Hover tooltip — portaled to document.body so it's never clipped by
          this container's `overflow: hidden` (needed for panning/zooming)
          or occluded by a neighboring node. Positioned from the hovered
          node's own getBoundingClientRect(), so it tracks the node
          correctly at any pan/zoom. */}
      {hoverInfo && (() => {
        const step = laid.find(s => s.id === hoverInfo.id);
        if (!step) return null;
        return hoverInfo.problemRect ? (
          <ProblemTooltip step={step} anchorRect={hoverInfo.problemRect} runInfo={runMap?.get(step.id)} />
        ) : (
          <StepTooltip step={step} anchorRect={hoverInfo.rect} runInfo={runMap?.get(step.id)} besideRight={vertical} />
        );
      })()}

      {/* Column focus bar */}
      {columnFocus && (
        <div
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
          style={{
            position: 'absolute', top: 12, left: 12, zIndex: 6, maxWidth: 'calc(100% - 24px)',
            display: 'flex', alignItems: 'center', gap: 8, padding: '5px 6px 5px 12px',
            background: C.bar, border: `1px solid ${C.borderStrong}`, borderRadius: 10,
            boxShadow: '0 8px 24px rgba(0,0,0,.35)', fontSize: '.8rem', color: C.fg2,
          }}
        >
          <span>Column <b style={{ color: C.fg1, fontFamily: "'JetBrains Mono', monospace" }}>{columnFocus.label}</b></span>
          {focusOrder.length ? (
            <>
              <span style={{ color: C.fg4 }}>
                {columnFocus.highlights.size === 1 ? '1 block' : `${columnFocus.highlights.size} blocks`} read or write it
              </span>
              <span style={{ minWidth: 44, textAlign: 'center', color: C.fg4, fontSize: '.74rem', fontFamily: "'JetBrains Mono', monospace" }}>
                {Math.min(focusIdx, focusOrder.length - 1) + 1} / {focusOrder.length}
              </span>
              <IconButton title="Previous block" onClick={() => goToFocus(focusIdx - 1)}><ChevronUp size={14} /></IconButton>
              <IconButton title="Next block" onClick={() => goToFocus(focusIdx + 1)}><ChevronDown size={14} /></IconButton>
            </>
          ) : (
            <span style={{ color: C.warn }}>No block reads or writes it</span>
          )}
          <IconButton title="Clear highlight (Esc)" onClick={() => onClearColumnFocus?.()}><X size={14} /></IconButton>
        </div>
      )}

      {/* Find bar */}
      {searchOpen && (
        <div
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
          style={{
            position: 'absolute', top: 12, right: 12, zIndex: 6,
            display: 'flex', alignItems: 'center', gap: 6, padding: '5px 6px 5px 10px',
            background: C.bar, border: `1px solid ${C.borderStrong}`, borderRadius: 10,
            boxShadow: '0 8px 24px rgba(0,0,0,.35)',
          }}
        >
          <Search size={14} style={{ color: C.fg5, flexShrink: 0 }} />
          <input
            ref={searchInputRef}
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); goToMatch(matchIdx + (e.shiftKey ? -1 : 1)); }
              else if (e.key === 'Escape') { e.preventDefault(); closeSearch(); }
            }}
            placeholder="Find a block… (lookup, scaling, trim)"
            style={{
              width: 230, background: 'transparent', border: 'none', outline: 'none',
              color: C.fg1, fontFamily: 'inherit', fontSize: '.82rem',
            }}
          />
          <span style={{
            minWidth: 52, textAlign: 'center', color: q && !matches.length ? C.warn : C.fg4,
            fontSize: '.74rem', fontFamily: "'JetBrains Mono', monospace",
          }}>
            {!q ? '' : matches.length ? `${Math.min(matchIdx, matches.length - 1) + 1} / ${matches.length}` : 'No match'}
          </span>
          <IconButton title="Previous match (Shift+Enter)" onClick={() => goToMatch(matchIdx - 1)}><ChevronUp size={14} /></IconButton>
          <IconButton title="Next match (Enter)" onClick={() => goToMatch(matchIdx + 1)}><ChevronDown size={14} /></IconButton>
          <IconButton title="Close (Esc)" onClick={closeSearch}><X size={14} /></IconButton>
        </div>
      )}

      {/* Floating canvas controls. Clicks (and presses) anywhere on this pane —
          buttons, the gaps between them, a disabled button — stay here, so
          they never count as an empty-canvas click (which deselects and
          closes the inspector) or start a canvas drag. */}
      <div
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        style={{
        position: 'absolute', bottom: 16, right: 16,
        display: 'flex', gap: 6, padding: 6,
        background: C.bar, border: `1px solid ${C.borderStrong}`, borderRadius: 10,
      }}>
        {onStepsPerRowChange && (
          <>
            <StepsPerRowField value={stepsPerRow} onChange={onStepsPerRowChange} />
            <div style={{ width: 1, alignSelf: 'stretch', background: C.border, margin: '2px 2px' }} />
          </>
        )}
        {onUndo && (
          <>
            <IconButton title="Undo (Ctrl+Z)" disabled={!canUndo}
              onClick={(e) => { e.stopPropagation(); onUndo(); }}><Undo2 size={14} /></IconButton>
            <IconButton title="Redo (Ctrl+Shift+Z)" disabled={!canRedo}
              onClick={(e) => { e.stopPropagation(); onRedo(); }}><Redo2 size={14} /></IconButton>
          </>
        )}
        <IconButton
          title="Find a block (Ctrl+F)"
          onClick={(e) => { e.stopPropagation(); setSearchOpen(true); setTimeout(() => searchInputRef.current?.focus(), 0); }}
        ><Search size={14} /></IconButton>
        <IconButton title="Zoom out" onClick={() => { userViewRef.current = true; setZoom(z => Math.max(MIN_ZOOM, z - 0.1)); }}>−</IconButton>
        <span
          title="Reset zoom (fit to screen)"
          onClick={(e) => { e.stopPropagation(); resetView(); }}
          style={{
            minWidth: 48, textAlign: 'center', alignSelf: 'center', cursor: 'pointer',
            color: C.fg4, fontSize: '.78rem', fontFamily: "'JetBrains Mono', monospace",
          }}>{Math.round(zoom * 100)}%</span>
        <IconButton title="Zoom in" onClick={() => { userViewRef.current = true; setZoom(z => Math.min(MAX_ZOOM, z + 0.1)); }}>+</IconButton>
        {onToggleFullscreen && (
          <IconButton
            title={isFullscreen ? 'Exit fullscreen (Esc)' : 'Fullscreen canvas'}
            onClick={(e) => { e.stopPropagation(); onToggleFullscreen(); }}
          >{isFullscreen ? <Minimize2 size={14} /> : <Maximize2 size={14} />}</IconButton>
        )}
      </div>

      {/* Keyboard hint bottom-left — not empty canvas either */}
      <div
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        style={{
        position: 'absolute', bottom: 16, left: 16,
        color: C.fg5, fontSize: '.7rem',
        background: C.bar + 'cc', border: `1px solid ${C.borderStrong}`,
        borderRadius: 8, padding: '5px 9px',
        fontFamily: "'JetBrains Mono', monospace",
      }}>
        drag · ⌘ + scroll · click{onOpenStep ? " · double-click to edit" : ""}
      </div>
    </div>
  );
}

// ── Left rail: full-height "Add step" panel ─────────────────────────────
// The pipeline's step list used to be duplicated here as a vertical outline
// above the palette — removed, since every step is already fully
// represented on the interactive flow canvas itself; this rail is now
// dedicated entirely to browsing/inserting new steps.
export function StepOutline({ pipeline, runMap, selectedId, onSelect, onAddStep, onCollapse, width = 260 }) {
  const addableCount = Object.values(STEP_TYPES).filter(v => v.cat !== 'ingestion' && v.cat !== 'output').length;
  return (
    <div style={{
      width, flexShrink: 0,
      background: C.bar,
      borderRight: `1px solid ${C.borderStrong}`,
      display: 'flex', flexDirection: 'column', minHeight: 0,
    }}>
      <div style={{ padding: '14px 16px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Eyebrow>Add step</Eyebrow>
        </div>
        <span style={{
          background: 'var(--info-bg)', color: C.accentBright,
          border: `1px solid ${C.accentMuted}`,
          fontSize: '.62rem', fontWeight: 700,
          padding: '1px 6px', borderRadius: 8, letterSpacing: '.04em',
          flexShrink: 0,
        }}>{addableCount}</span>
        {onCollapse && (
          <IconButton title="Hide add-step panel (⌥1)" onClick={onCollapse} style={{ flexShrink: 0 }}>‹</IconButton>
        )}
      </div>

      <StepPalette onAddStep={onAddStep} pipeline={pipeline} selectedId={selectedId} />
    </div>
  );
}

// ── Categorised step palette ────────────────────────────────────────────
export function StepPalette({ onAddStep, onClose, pipeline, selectedId }) {
  const [query, setQuery] = React.useState('');
  // Every category starts collapsed — a category only opens when the user
  // clicks its header (or, below, while a search query is active, so a
  // match isn't hidden inside a collapsed group).
  const [openCat, setOpenCat] = React.useState({});
  const toggleCat = (k) => setOpenCat(o => ({ ...o, [k]: !o[k] }));

  // Group step types by category
  const byCat = React.useMemo(() => {
    const out = {};
    for (const [k, v] of Object.entries(STEP_TYPES)) {
      if (!out[v.cat]) out[v.cat] = [];
      out[v.cat].push([k, v]);
    }
    return out;
  }, []);

  // Every addable category (excludes the hidden ingestion/output groups,
  // same filter the render below applies) — what "Expand all"/"Collapse
  // all" act on.
  const addableCatKeys = Object.keys(CATEGORIES).filter(k => k !== 'ingestion' && k !== 'output');
  const expandAll = () => setOpenCat(Object.fromEntries(addableCatKeys.map(k => [k, true])));
  const collapseAll = () => setOpenCat(Object.fromEntries(addableCatKeys.map(k => [k, false])));

  const q = query.trim().toLowerCase();
  const matches = (k, v) => !q || k.includes(q) || v.label.toLowerCase().includes(q) || v.shortDesc?.toLowerCase().includes(q);

  const addableCount = Object.values(STEP_TYPES).filter(v => v.cat !== 'ingestion' && v.cat !== 'output').length;

  return (
    <div style={{
      flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column',
    }}>
      <div style={{ padding: '10px 12px 8px', flexShrink: 0 }}>
        <div style={{ position: 'relative' }}>
          <span style={{
            position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)',
            color: C.fg5, fontSize: '.78rem',
          }}>⌕</span>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={`Search ${addableCount} step types…`}
            style={{
              width: '100%', boxSizing: 'border-box', background: C.input,
              border: `1px solid ${C.borderStrong}`, borderRadius: 6,
              padding: query ? '6px 24px 6px 28px' : '6px 8px 6px 28px',
              color: C.fg1,
              fontSize: '.78rem', fontFamily: 'inherit', outline: 'none',
            }}
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery('')}
              title="Clear search"
              style={{
                position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: 16, height: 16, padding: 0, border: 'none', borderRadius: 4,
                background: 'transparent', color: C.fg5, cursor: 'pointer',
              }}
            ><X size={12} /></button>
          )}
        </div>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: '0 8px 10px' }}>
        {Object.entries(CATEGORIES).map(([catKey, cat]) => {
          const items = (byCat[catKey] || []).filter(([k, v]) => matches(k, v));
          if (catKey === 'ingestion' || catKey === 'output') {
            // Hide ingestion + output from the add menu (they're terminal nodes)
            return null;
          }
          if (!items.length) return null;
          const isOpen = openCat[catKey] !== false && (!!q || openCat[catKey]);
          return (
            <div key={catKey} style={{ marginBottom: 6 }}>
              <button
                onClick={() => toggleCat(catKey)}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', gap: 8,
                  background: 'transparent', border: 'none', cursor: 'pointer',
                  padding: '6px 4px', fontFamily: 'inherit',
                  color: C.fg3, fontSize: '.7rem', fontWeight: 700,
                  textTransform: 'uppercase', letterSpacing: '.08em',
                }}>
                <span style={{
                  width: 7, height: 7, borderRadius: 2, background: cat.accent,
                }} />
                <span style={{ flex: 1, textAlign: 'left' }}>{cat.label}</span>
                <span style={{ color: C.fg5 }}>{items.length}</span>
                <span style={{
                  display: 'inline-block', color: C.fg5, fontSize: '.7rem',
                  transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                  transition: 'transform .15s',
                }}>›</span>
              </button>
              {isOpen && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 3, paddingLeft: 4 }}>
                  {/* Top-level items without a subCat */}
                  {items.filter(([_, v]) => !v.subCat).map(([k, v]) => (
                    <button
                      key={k}
                      onClick={PHASE2_STEPS.has(k) ? undefined : () => { onAddStep?.(k); onClose?.(); }}
                      draggable={!PHASE2_STEPS.has(k)}
                      onDragStart={(e) => startPaletteDrag(e, k)}
                      disabled={PHASE2_STEPS.has(k)}
                      title={PHASE2_STEPS.has(k) ? PHASE2_TOOLTIP : v.shortDesc}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        background: C.surface, color: C.fg2,
                        border: `1px solid ${C.border}`,
                        borderLeft: `3px solid ${cat.accent}`,
                        borderRadius: 6, padding: '6px 9px',
                        fontFamily: 'inherit', fontSize: '.76rem', fontWeight: 600,
                        cursor: PHASE2_STEPS.has(k) ? 'not-allowed' : 'pointer', opacity: PHASE2_STEPS.has(k) ? .45 : 1, textAlign: 'left',
                        transition: 'background-color .12s, border-color .12s',
                      }}
                      onMouseEnter={(e) => {
                        if (PHASE2_STEPS.has(k)) return;
                        e.currentTarget.style.borderColor = C.accentMuted;
                        e.currentTarget.style.background = 'var(--info-bg)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = C.border;
                        e.currentTarget.style.background = C.surface;
                      }}
                    >
                      <span style={{ display: 'inline-flex', width: 16, justifyContent: 'center', flexShrink: 0, color: cat.accent }}><StepIcon name={v.icon} size={14} /></span>
                      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v.label}</span>
                      {PHASE2_STEPS.has(k) && <span style={{ fontSize: '.6rem', fontWeight: 700, color: C.fg5, border: `1px solid ${C.border}`, borderRadius: 4, padding: '1px 5px', flexShrink: 0 }}>{PHASE2_LABEL}</span>}
                    </button>
                  ))}

                  {/* Nested sub-categories */}
                  {cat.subCategories && Object.entries(cat.subCategories).map(([subKey, sub]) => {
                    const subItems = items.filter(([_, v]) => v.subCat === subKey);
                    if (!subItems.length) return null;
                    return (
                      <div key={subKey} style={{ marginTop: 4, marginBottom: 4 }}>
                        <div style={{
                          fontSize: '.7rem', color: C.fg4, fontWeight: 700,
                          textTransform: 'uppercase', paddingLeft: 4, marginBottom: 6,
                          display: 'flex', alignItems: 'center', gap: 6, letterSpacing: '.05em'
                        }}>
                          <span style={{ display: 'inline-flex' }}><StepIcon name={sub.icon} size={13} /></span>
                          <span>{sub.label}</span>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 3, paddingLeft: 12 }}>
                          {subItems.map(([k, v]) => (
                            <button
                              key={k}
                              onClick={PHASE2_STEPS.has(k) ? undefined : () => { onAddStep?.(k); onClose?.(); }}
                              draggable={!PHASE2_STEPS.has(k)}
                              onDragStart={(e) => startPaletteDrag(e, k)}
                              disabled={PHASE2_STEPS.has(k)}
                              title={PHASE2_STEPS.has(k) ? PHASE2_TOOLTIP : v.shortDesc}
                              style={{
                                display: 'flex', alignItems: 'center', gap: 8,
                                background: C.surface, color: C.fg2,
                                border: `1px solid ${C.border}`,
                                borderLeft: `3px solid ${cat.accent}`,
                                borderRadius: 6, padding: '6px 9px',
                                fontFamily: 'inherit', fontSize: '.76rem', fontWeight: 600,
                                cursor: PHASE2_STEPS.has(k) ? 'not-allowed' : 'pointer', opacity: PHASE2_STEPS.has(k) ? .45 : 1, textAlign: 'left',
                                transition: 'background-color .12s, border-color .12s',
                              }}
                              onMouseEnter={(e) => {
                                if (PHASE2_STEPS.has(k)) return;
                                e.currentTarget.style.borderColor = C.accentMuted;
                                e.currentTarget.style.background = 'var(--info-bg)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.borderColor = C.border;
                                e.currentTarget.style.background = C.surface;
                              }}
                            >
                              <span style={{ display: 'inline-flex', width: 16, justifyContent: 'center', flexShrink: 0, color: cat.accent }}><StepIcon name={v.icon} size={14} /></span>
                              <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v.label}</span>
                              {PHASE2_STEPS.has(k) && <span style={{ fontSize: '.6rem', fontWeight: 700, color: C.fg5, border: `1px solid ${C.border}`, borderRadius: 4, padding: '1px 5px', flexShrink: 0 }}>{PHASE2_LABEL}</span>}
                            </button>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
        {q && Object.values(byCat).flat().filter(([k, v]) => matches(k, v)).length === 0 && (
          <div style={{ color: C.fg5, fontSize: '.78rem', padding: '20px 8px', textAlign: 'center' }}>
            No step matches "<span style={{ color: C.fg3 }}>{query}</span>"
          </div>
        )}
      </div>
      <div style={{
        display: 'flex', justifyContent: 'flex-end', gap: 4,
        padding: '6px 8px', borderTop: `1px solid ${C.border}`, flexShrink: 0,
      }}>
        <IconButton title="Expand all categories" onClick={expandAll}><ChevronsDown size={14} /></IconButton>
        <IconButton title="Collapse all categories" onClick={collapseAll}><ChevronsUp size={14} /></IconButton>
      </div>
    </div>
  );
}
